import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set default auth header from stored token
const storedToken = localStorage.getItem('xo_token');
if (storedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('xo_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      localStorage.removeItem('xo_token');
      localStorage.removeItem('xo_refresh_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.token) {
        localStorage.setItem('xo_token', data.token);
        localStorage.setItem('xo_refresh_token', data.refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      }
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
      if (data.token) {
        localStorage.setItem('xo_token', data.token);
        localStorage.setItem('xo_refresh_token', data.refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      }
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error('Logout error:', e);
    }
    localStorage.removeItem('xo_token');
    localStorage.removeItem('xo_refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
